"use client";

import { useEffect, type RefObject } from "react";

declare global {
  interface Window {
    adsbygoogle: unknown[];
  }
}

// 폭이 확정될 때까지 대기하는 최대 프레임 수(약 1초). 모달 마운트/애니메이션 동안
// width=0인 시점을 넘긴 뒤 push하기 위함. 끝까지 0이면(영구 display:none 등) 그냥 push.
const MAX_FRAMES = 60;

/**
 * 단일 `<ins class="adsbygoogle">` 요소를 초기화하는 훅 (JCG-2057).
 *
 * `(window.adsbygoogle = window.adsbygoogle || []).push({})`는 호출한 컴포넌트의
 * ins가 아니라 **페이지 전역에서 DOM 순서상 다음 미초기화 ins**를 채운다(positional).
 * 따라서 "보이는 ins 1개당 정확히 1회 push"가 지켜져야 하고, push 처리 시점에
 * ins 폭이 0이면(모달 마운트 중 등) availableWidth=0으로 미충전된다.
 *
 * 이 훅은:
 * 1. ref가 가리키는 ins의 offsetWidth가 0이면 rAF로 폭이 확정될 때까지 대기하고,
 * 2. 이미 `data-adsbygoogle-status`가 있으면(다른 push로 채워졌으면) 중복 push를 막고,
 * 3. 조건 충족 시 push를 정확히 1회 실행한다.
 *
 * @param insRef   대상 ins 요소 ref
 * @param enabled  광고를 띄울 조건(이용권/로드/슬롯/모달 open 등). false면 아무것도 안 함.
 * @param resetKey 같은 ins ref를 유지한 채 대상이 바뀔 때(예: 모바일↔데스크톱 슬롯 교체)
 *                 effect를 재실행시키기 위한 키. 값이 바뀌면 이전 대기를 취소하고 다시 시도.
 */
export function useAdSensePush(
  insRef: RefObject<HTMLModElement | null>,
  enabled: boolean,
  resetKey?: string | number | boolean,
): void {
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;

    let frameId = 0;
    let cancelled = false;
    let frames = 0;

    const tryPush = () => {
      if (cancelled) return;
      const ins = insRef.current;
      if (!ins) return;
      // 다른 ins의 push로 이미 채워졌으면 중복 push 금지
      if (ins.getAttribute("data-adsbygoogle-status")) return;
      // 폭이 확정되기 전(모달 마운트/애니메이션 중 width=0)에는 대기
      if (ins.offsetWidth === 0 && frames < MAX_FRAMES) {
        frames += 1;
        frameId = window.requestAnimationFrame(tryPush);
        return;
      }
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (err) {
        console.error("AdSense push error:", err);
      }
    };

    frameId = window.requestAnimationFrame(tryPush);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frameId);
    };
  }, [enabled, insRef, resetKey]);
}
